import React from 'react';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { motion } from 'framer-motion';
import setup from '../assets/data/setup.json';
import MotionSection from './ui/MotionSection';
import SectionHeader from './ui/SectionHeader';
import TechBadge from './ui/TechBadge';
import CompanyLogo from './ui/CompanyLogo';

type Experience = (typeof setup.experiences)[number] & {
  logoDomain?: string;
  majorContributions?: string[];
};

function Timeline() {
  const experiences = setup.experiences as Experience[];

  return (
    <MotionSection id="experience" muted>
      <SectionHeader
        eyebrow="Career"
        title="Experience"
        subtitle="Building enterprise software that solves business problems—not just writing code."
      />
      <div className="space-y-6">
        {experiences.map((exp, index) => (
          <motion.article
            key={exp.subtitle}
            initial={{ opacity: 0, y: 20, filter: 'blur(4px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.08 }}
            className="ui-card ui-card-lift p-5 sm:p-7"
          >
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
              <div className="flex gap-4">
                <CompanyLogo
                  name={exp.subtitle}
                  domain={
                    exp.logoDomain ||
                    (exp.link
                      ? (() => {
                          try {
                            return new URL(exp.link).hostname.replace('www.', '');
                          } catch {
                            return undefined;
                          }
                        })()
                      : undefined)
                  }
                />
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-semibold text-ink tracking-[-0.01em]">
                      {exp.role || exp.subtitle}
                    </h3>
                    {exp.link && (
                      <a
                        href={exp.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-ink-muted hover:text-accent transition-colors"
                        aria-label={`Visit ${exp.subtitle}`}
                      >
                        <OpenInNewIcon sx={{ fontSize: 16 }} />
                      </a>
                    )}
                  </div>
                  <p className="text-sm text-ink-muted">{exp.subtitle}</p>
                  <p className="text-accent text-sm font-medium mt-0.5">
                    {exp.highlight}
                  </p>
                </div>
              </div>
              <div className="text-sm text-ink-muted sm:text-right sm:pl-4">
                <div>{exp.date}</div>
                <div>{exp.employmentType}</div>
                <div>{exp.country}</div>
              </div>
            </div>

            <ul className="space-y-2 mb-5">
              {exp.bulletPoints.map((point, idx) => (
                <li
                  key={idx}
                  className="text-sm sm:text-[15px] text-ink-muted leading-relaxed pl-4 relative before:content-[''] before:absolute before:left-0 before:top-2.5 before:w-1 before:h-1 before:rounded-full before:bg-ink-muted"
                >
                  {point}
                </li>
              ))}
            </ul>

            {exp.businessImpact && exp.businessImpact.length > 0 && (
              <div className="mb-5 p-4 rounded-lg bg-accent-muted/50 border border-accent/10">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUpIcon sx={{ fontSize: 16 }} className="text-accent" />
                  <h4 className="text-xs font-semibold uppercase tracking-[0.1em] text-accent">
                    Business Impact
                  </h4>
                </div>
                <ul className="space-y-1.5">
                  {exp.businessImpact.map((impact) => (
                    <li key={impact} className="text-sm text-ink-secondary leading-relaxed pl-4 relative before:content-[''] before:absolute before:left-0 before:top-2.5 before:w-1 before:h-1 before:rounded-full before:bg-accent">
                      {impact}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {exp.technologies && (
              <div className="flex flex-wrap gap-2 mb-4">
                {exp.technologies.map((tech) => (
                  <TechBadge key={tech} label={tech} />
                ))}
              </div>
            )}

            {(exp as Experience).majorContributions && (
              <div className="mb-4 p-4 rounded-lg border border-line bg-canvas-muted/40">
                <h4 className="text-xs font-semibold uppercase tracking-[0.1em] text-ink-muted mb-3">
                  Major Contributions
                </h4>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {exp.majorContributions!.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-ink-secondary">
                      <span className="text-accent font-medium mt-0.5">✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(exp.highlights || exp.projects) && (
              <div className="flex flex-wrap gap-2 pt-2 border-t border-line">
                {(exp.highlights || exp.projects || []).map((item) => (
                  <span
                    key={item}
                    className="text-xs text-ink-muted px-2 py-1 rounded-md bg-canvas-muted border border-line"
                  >
                    {item}
                  </span>
                ))}
              </div>
            )}
          </motion.article>
        ))}
      </div>
    </MotionSection>
  );
}

export default Timeline;
